import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { QueryHookOptions, useQuery } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  CycleStatusesFeatureDocument,
  CycleStatusesFeatureQuery,
  CycleStatusesFeatureQueryVariables,
  CyclesListDocument,
  PayrollRunCycleListFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { CyclesListFilterOptions } from 'modules/compensations/hooks/useCyclesListFilterOptions'
import {
  getPayrollRunStatusText,
  getPayrollRunStatusVariant,
} from 'modules/compensations/lib/payrollRunStatus'
import { isFeatureEnabled } from 'modules/compensations/lib/utils'

function useCycleStatusesFeatureQuery(
  baseOptions: QueryHookOptions<CycleStatusesFeatureQuery, CycleStatusesFeatureQueryVariables>
) {
  const options = { ...baseOptions }
  return useQuery<CycleStatusesFeatureQuery, CycleStatusesFeatureQueryVariables>(
    CycleStatusesFeatureDocument,
    options
  )
}

const capitalize = (value: string) => {
  return `${value.charAt(0).toUpperCase()}${value.slice(1).toLowerCase()}`
}

type CyclesListProps = Pick<
  CyclesListFilterOptions,
  'currencyFilterOptions' | 'countryFilterOptions'
>
function CyclesList({ countryFilterOptions, currencyFilterOptions }: CyclesListProps) {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { company } = useCurrentSession()
  const {
    data: featuresData,
    loading,
    error,
  } = useCycleStatusesFeatureQuery({
    variables: { companyId: company.id },
  })

  const isPayrollRunTimelineFeatureEnabled = isFeatureEnabled(
    'dev_compensations_payrollrun_timeline',
    featuresData?.features?.featuresConnection
  )

  const presets = useMemo(() => {
    return countryFilterOptions.map((option) => ({
      label: option.label,
      filter: { country: [option.value] },
    }))
  }, [countryFilterOptions])
  const source = useGraphqlDataCollectionSource(
    {
      document: CyclesListDocument,
      fetchPolicy: 'cache-and-network',
      mapData: (data) => data.compensations.payrollRunsConnection.nodes,
      mapPagination: (data) => {
        return {
          total: data.compensations.payrollRunsConnection.totalCount ?? 0,
        }
      },
      navigationFilters: {
        date: {
          type: 'date-navigator',
          defaultValue: new Date(),
          granularity: 'year',
        },
      },
      totalItemSummary: (count) => i18n.t('compensations.cyclesList.totalElements', { count }),
      mapVariables: ({ navigationFilters, filters: { country, currency } }) => {
        return {
          dateFrom: i18nDateToString(navigationFilters?.date?.value?.from ?? new Date(), 'backend'),
          dateTo: i18nDateToString(navigationFilters?.date?.value?.to ?? new Date(), 'backend'),
          countries: country,
          currencies: currency,
        }
      },
      filters: {
        country: {
          label: i18n.t('compensations.cyclesList.countryColumnLabel'),
          type: 'in',
          options: {
            options: countryFilterOptions,
          },
        },
        currency: {
          label: i18n.t('compensations.cyclesList.currencyFilterLabel'),
          type: 'in',
          options: {
            options: currencyFilterOptions,
          },
        },
      },
      // TODO: to add the itemsCount we would need to groupBy country and using the CountryPresets query already created
      presets,
      itemOnClick: (item) => () =>
        item.id &&
        navigate(
          navigation.compensations.cycles.id.fullPath({
            id: String(item.id),
          })
        ),
    },
    [countryFilterOptions, currencyFilterOptions]
  )

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('compensations.cyclesList.periodColumnLabel'),
                render: (item) => {
                  return {
                    type: 'text',
                    value: item.label,
                  }
                },
              },
              {
                label: i18n.t('compensations.cyclesList.countryColumnLabel'),
                render: (item) => {
                  return {
                    type: 'text',
                    value: `${capitalize(item.cycle?.countryLabel ?? '')} (${(
                      item.cycle?.currency ?? ''
                    ).toUpperCase()})`,
                  }
                },
              },
              ...(isPayrollRunTimelineFeatureEnabled
                ? [
                    {
                      label: i18n.t('compensations.cyclesList.statusColumnLabel'),
                      render: (item: PayrollRunCycleListFragment) => {
                        return {
                          type: 'status' as const,
                          value: {
                            label: getPayrollRunStatusText(item.status),
                            status: getPayrollRunStatusVariant(item.status),
                          },
                        }
                      },
                    },
                  ]
                : []),
            ],
          },
        },
      ]}
    />
  )
}

export { CyclesList }
